/**
 * history.ts — snapshot-based undo/redo for the editor surface.
 *
 * Every per-graph edit op that crosses the adapter seam is captured here as a
 * bundle snapshot taken BEFORE the mutation lands. Undo replays the snapshot
 * through the adapter's existing `replaceBundle` op, so the mechanism works
 * unchanged for the in-browser LocalAdapter AND the WS ApiAdapter (the server
 * + mock server already implement `replaceBundle`) — zero wire-contract
 * changes.
 *
 * Two semantics worth knowing:
 *
 * - **Coalescing.** The node/edge modals commit on every keystroke (the draft
 *   → onUpdate path), so without merging, one word typed in a label field
 *   would be a dozen undo steps. Consecutive edits on the same ENTITY within
 *   COALESCE_MS collapse into the FIRST snapshot — one undo step per editing
 *   burst, including multi-op gestures (an auto-routed edge grab fires
 *   `updateEdgeHandles` + `setBends`, then a `setBends` per pointermove).
 *   Structural ops (add/remove/rename) never coalesce.
 *
 * - **Barriers.** Graph-collection ops (addGraph/removeGraph/renameGraph) and
 *   external `replaceBundle` calls (imports, save-as navigation) clear the
 *   stacks: restoring a removed graph has no per-graph op to replay through,
 *   and after a wholesale replace the old snapshots describe another document.
 *
 * Known trade-off (deliberate): `replaceBundle` resets the sim run state
 * (stocks/step). For structural undo that is the correct semantic anyway — the
 * old run was computed under the old graph.
 */

import type { BackendAdapter } from './adapter';
import type { ScenarioBundle } from './types';

/** Consecutive same-key edits within this window merge into one undo step. */
const COALESCE_MS = 600;
/** Upper bound on remembered steps; oldest are dropped first. */
const MAX_DEPTH = 100;

interface HistoryEntry {
  graphId: string;
  /** Coalesce key: the primary entity id ('' for whole-list ops). */
  key: string;
  /** When the burst started (extended while the burst continues). */
  time: number;
  /** Bundle snapshot taken before the mutation. Owned by the history. */
  bundle: ScenarioBundle;
}

/** Injectable clock so tests can simulate the coalesce window deterministically. */
export type NowFn = () => number;

export class EditHistory {
  private undoStack: HistoryEntry[] = [];
  private redoStack: HistoryEntry[] = [];
  private readonly now: NowFn;

  constructor(now: NowFn = () => Date.now()) {
    this.now = now;
  }

  /** Record the pre-mutation snapshot. Call BEFORE forwarding the edit. */
  capture(graphId: string, key: string, bundle: ScenarioBundle, structural = false): void {
    const t = this.now();
    const top = this.undoStack[this.undoStack.length - 1];
    if (!structural && top && top.graphId === graphId && top.key === key && t - top.time <= COALESCE_MS) {
      top.time = t; // rolling window: keep merging while the burst continues
      return;
    }
    this.redoStack = []; // a new edit invalidates the redo path
    this.undoStack.push({ graphId, key, time: t, bundle });
    if (this.undoStack.length > MAX_DEPTH) this.undoStack.shift();
  }

  /** Pop the next undo target (the pre-edit snapshot to restore). */
  popUndo(): HistoryEntry | null {
    return this.undoStack.pop() ?? null;
  }

  /** Pop the next redo target (the post-edit snapshot stashed by undo). */
  popRedo(): HistoryEntry | null {
    return this.redoStack.pop() ?? null;
  }

  /** Stash the current state on the undo stack during a redo (no coalescing,
   *  no redo-clear — the redo/undo stacks just swap roles). */
  pushForRedo(graphId: string, bundle: ScenarioBundle): void {
    this.undoStack.push({ graphId, key: 'redo', time: this.now(), bundle });
    if (this.undoStack.length > MAX_DEPTH) this.undoStack.shift();
  }

  /** Stash the current state on the redo stack during an undo. */
  pushForUndo(graphId: string, bundle: ScenarioBundle): void {
    this.redoStack.push({ graphId, key: 'undo', time: this.now(), bundle });
    if (this.redoStack.length > MAX_DEPTH) this.redoStack.shift();
  }

  canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  /** History reset — called on barrier ops (see the file header). */
  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
  }
}

/** The undo controls the wrapper adds on top of the BackendAdapter surface. */
export interface HistoryControls {
  undo(): void;
  redo(): void;
  canUndo(): boolean;
  canRedo(): boolean;
}

/** Extract a stable identity from an op's primary argument: a bare id string,
 *  or the `id`/`key` field of the passed entity object. */
function argId(v: unknown): string {
  if (typeof v === 'string') return v;
  if (v !== null && typeof v === 'object') {
    if ('id' in v) return String((v as { id: unknown }).id);
    if ('key' in v) return String((v as { key: unknown }).key);
  }
  return '?';
}

/** Editor methods whose calls capture a pre-mutation snapshot. `key` derives
 *  the coalesce identity from the call's primary argument (the ENTITY id —
 *  deliberately not the method: a single gesture often fires several ops on
 *  one entity, e.g. grabbing an auto-routed bend fires `updateEdgeHandles` +
 *  `setBends` then more `setBends` per pointermove, and that whole burst is
 *  one logical edit = one undo step). `structural` ops never merge. */
const EDIT_CAPTURES: Record<string, { key: (args: unknown[]) => string; structural?: boolean }> = {
  // Structural (each its own undo step)
  addNode: { key: (a) => argId(a[0]), structural: true },
  removeNode: { key: (a) => argId(a[0]), structural: true },
  duplicateNode: { key: (a) => argId(a[0]), structural: true },
  renameNode: { key: (a) => `${a[0]}`, structural: true },
  addEdge: { key: (a) => argId(a[0]), structural: true },
  removeEdge: { key: (a) => argId(a[0]), structural: true },
  renameEdge: { key: (a) => `${a[0]}`, structural: true },
  addGroup: { key: (a) => argId(a[0]), structural: true },
  addNodeGroup: { key: (a) => argId(a[0]), structural: true },
  removeNodeGroup: { key: (a) => argId(a[0]), structural: true },
  renameGroup: { key: (a) => `${a[0]}`, structural: true },
  addMetric: { key: (a) => argId(a[0]), structural: true },
  removeMetric: { key: (a) => argId(a[0]), structural: true },
  renameMetric: { key: (a) => `${a[0]}`, structural: true },
  // Continuous edits (coalesce into one step per burst)
  updateNode: { key: (a) => argId(a[0]) },
  updateEdge: { key: (a) => argId(a[0]) },
  setEdgeGroup: { key: (a) => argId(a[0]) },
  updateEdgeHandles: { key: (a) => argId(a[0]) },
  setBends: { key: (a) => argId(a[0]) },
  setLabelT: { key: (a) => argId(a[0]) },
  updateNodeGroup: { key: (a) => argId(a[0]) },
  updateMetric: { key: (a) => argId(a[0]) },
  setParamKey: { key: (a) => `${a[1]}` },
  // Whole-list ops have no entity id — synthetic keys, prefixed so they can
  // never collide with a real node/edge id.
  setLegend: { key: () => '\u0000legend' },
  setKnobs: { key: () => '\u0000knobs' },
  setWorkflowTriggers: { key: () => '\u0000workflowTriggers' },
};

/** Ops that reset history instead of capturing (see the file header). */
const BARRIER_OPS = new Set(['replaceBundle', 'addGraph', 'removeGraph', 'renameGraph']);

/** Read the active graph's bundle WITHOUT wire side effects — unlike
 *  `exportBundle` (which the ApiAdapter turns into a WS request), getProject
 *  is a pure local-state read on every adapter. */
function activeBundleOf(adapter: BackendAdapter): { graphId: string; bundle: ScenarioBundle } | null {
  const graphId = adapter.activeGraphId?.();
  const project = adapter.getProject?.();
  if (!graphId || !project) return null;
  const bundle = project.graphs.find((b) => b.id === graphId);
  return bundle ? { graphId, bundle } : null;
}

function clone(bundle: ScenarioBundle): ScenarioBundle {
  return structuredClone(bundle);
}

/**
 * Wrap an adapter so every editor op captures an undo snapshot before it
 * mutates. The returned object implements `BackendAdapter & HistoryControls`;
 * non-editor members pass straight through to the wrapped adapter.
 *
 * `undo`/`redo` talk to the RAW adapter (not the wrapper) when restoring —
 * replaying through the wrapper's `replaceBundle` would trip the barrier and
 * wipe the history being navigated.
 */
export function withEditHistory(
  adapter: BackendAdapter,
  history: EditHistory,
): BackendAdapter & HistoryControls {
  const captureThen =
    <T>(method: (...args: unknown[]) => T, name: string): ((...args: unknown[]) => T) =>
    (...args: unknown[]) => {
      const active = activeBundleOf(adapter);
      if (active) {
        const spec = EDIT_CAPTURES[name];
        history.capture(active.graphId, spec.key(args), clone(active.bundle), spec.structural);
      }
      return method.apply(adapter, args);
    };

  const barrierThen =
    (method: (...args: unknown[]) => unknown): ((...args: unknown[]) => unknown) =>
    (...args: unknown[]) => {
      history.clear();
      return method.apply(adapter, args);
    };

  const controls: HistoryControls = {
    undo(): void {
      const entry = history.popUndo();
      if (!entry) return;
      // Stash the CURRENT state for redo before any graph switch moves it.
      const current = activeBundleOf(adapter);
      if (current) history.pushForUndo(current.graphId, clone(current.bundle));
      if (entry.graphId && entry.graphId !== adapter.activeGraphId?.()) {
        adapter.setActiveGraph?.(entry.graphId);
      }
      adapter.replaceBundle?.(clone(entry.bundle));
    },
    redo(): void {
      const entry = history.popRedo();
      if (!entry) return;
      const current = activeBundleOf(adapter);
      if (current) history.pushForRedo(current.graphId, clone(current.bundle));
      if (entry.graphId && entry.graphId !== adapter.activeGraphId?.()) {
        adapter.setActiveGraph?.(entry.graphId);
      }
      adapter.replaceBundle?.(clone(entry.bundle));
    },
    canUndo: () => history.canUndo(),
    canRedo: () => history.canRedo(),
  };

  return new Proxy(adapter, {
    get(target, prop) {
      if (prop === 'undo' || prop === 'redo' || prop === 'canUndo' || prop === 'canRedo') {
        return controls[prop];
      }
      const value = Reflect.get(target, prop, target);
      if (typeof value !== 'function') return value;
      const name = String(prop);
      if (EDIT_CAPTURES[name]) return captureThen(value as (...args: unknown[]) => unknown, name);
      if (BARRIER_OPS.has(name)) return barrierThen(value as (...args: unknown[]) => unknown);
      return value;
    },
  }) as BackendAdapter & HistoryControls;
}
